/**
 * ContextMenu — minimal absolute-positioned right-click menu.
 *
 * Usage:
 *   const [ctx, setCtx] = useState(null);          // {x, y, items}
 *   <div onContextMenu={(e) => { e.preventDefault();
 *        setCtx({ x: e.clientX, y: e.clientY,
 *                  items: [{label: "Duplicate", onClick: () => duplicate(id)}] }); }} />
 *   <ContextMenu menu={ctx} onClose={() => setCtx(null)} />
 *
 * Items: [{label, onClick, danger?, disabled?, icon?}]
 */
import { useEffect } from "react";

export default function ContextMenu({ menu, onClose }) {
  // Auto-close on any document click, scroll or Escape — same UX as native menus.
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => onClose();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  // Keep the menu inside the viewport — flip on the right edge. Guard against
  // non-finite client coords (programmatic dispatch / missing event) so the
  // `left/top` CSS props never resolve to NaN.
  const W = 200, H = (menu.items?.length || 0) * 32 + 8;
  const mx = Number.isFinite(menu.x) ? menu.x : 0;
  const my = Number.isFinite(menu.y) ? menu.y : 0;
  const x = Math.max(0, Math.min(mx, window.innerWidth  - W - 4));
  const y = Math.max(0, Math.min(my, window.innerHeight - H - 4));

  return (
    <div
      className="fixed z-[2000] min-w-[200px] bg-[#0a0d12] border border-white/10 shadow-2xl py-1 font-mono text-xs"
      style={{ left: x, top: y }}
      data-testid="ctx-menu"
      onClick={(e) => e.stopPropagation()}
    >
      {menu.items.map((it, i) => (
        <button
          key={i}
          disabled={it.disabled}
          onClick={() => { it.onClick && it.onClick(); onClose(); }}
          className={
            `w-full text-left px-3 py-1.5 flex items-center gap-2 ` +
            (it.disabled ? "opacity-40 cursor-not-allowed " :
             it.danger   ? "hover:bg-red-500/15 text-red-300 " :
                            "hover:bg-[#00E5FF]/15 text-gray-200 ")
          }
          data-testid={`ctx-item-${(it.label || "").toLowerCase().replace(/\s+/g, "-")}`}
        >
          {it.icon}
          <span>{it.label}</span>
          {it.hint && <span className="ml-auto text-[10px] text-gray-500">{it.hint}</span>}
        </button>
      ))}
    </div>
  );
}
